import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import supabaseClient from '@/api/supabaseClient.ts';
import Radio from '@/components/shared/Radio/Radio';
import LongAssessmentModal from './modals/InstructionsModal/LongAssessmentModal';
import longAssessments from '@/_constants/LongAssessments.json';

interface Subdomain {
  id: number;
  name: string;
  discovery_description: string;
  foundational_description: string;
  emerging_description: string;
  developing_description: string;
  advancing_description: string;
  thriving_description: string;
  not_applicable_description: string;
  score: number | null;
}

const AssessmentSteps: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { domainId, step } = useParams<{ domainId: string; step: string }>();
  const [subdomains, setSubdomains] = useState<Subdomain[]>([]);
  const [domainName, setDomainName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [selectedAnswers, setSelectedAnswers] = useState<{ [key: number]: number }>({});
  const [saving, setSaving] = useState(false);
  const [userAssessmentId, setUserAssessmentId] = useState<string | null>(null);
  const currentStep = parseInt(step || '1');
  const currentSubdomain = subdomains[currentStep - 1];
  const isLastStep = currentStep === subdomains.length;
  const isFirstStep = currentStep === 1;

  useEffect(() => {
    const fetchAssessmentData = async () => {
      if (!domainId) return;
      
      try {
        const { data: domainData, error: domainError } = await supabaseClient
          .from('domains')
          .select('name')
          .eq('id', parseInt(domainId))
          .maybeSingle();

        if (domainError || !domainData) {
          return;
        }

        setDomainName(domainData.name);

        const { data: subdomainsData, error: subdomainsError } = await supabaseClient
          .from('subdomains')
          .select('*')
          .eq('domain_id', parseInt(domainId))
          .order('id');

        if (subdomainsError) {
          return;
        }

        setSubdomains(subdomainsData || []);
    
        const userId = await getCurrentUserId(); 
        const assessmentId = await getOrCreateUserAssessment(userId, domainData.name);
        setUserAssessmentId(assessmentId);
        
        const { data: responsesData, error: responsesError } = await supabaseClient
          .from('user_responses')
          .select('subdomain_id, selected_level')
          .eq('user_assessment_id', assessmentId);

        if (responsesError) {
          return;
        }

        const initialAnswers: { [key: number]: number } = {};
        responsesData?.forEach((response) => {
          initialAnswers[response.subdomain_id] = response.selected_level;
        });
        setSelectedAnswers(initialAnswers);
        
      } catch {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    };

    fetchAssessmentData();
  }, [domainId, t]);

  const getCurrentUserId = async (): Promise<string> => {
    const { data: { user } } = await supabaseClient.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data: existingUser, error: fetchError } = await supabaseClient
      .from('assessment_users')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();

    if (fetchError) {
      throw fetchError;
    }

    if (existingUser) {
      return existingUser.id;
    }

    const { data: newUser, error: createError } = await supabaseClient
      .from('assessment_users')
      .insert({
        id: user.id,
        email: user.email || '',
        full_name: user.user_metadata?.full_name || user.email || ''
      })
      .select('id')
      .maybeSingle();

    if (createError) {
      throw createError;
    }

    return newUser?.id || '';
  };

  const getOrCreateUserAssessment = async (userId: string, assessmentName: string): Promise<string> => {
    const { data: existingAssessment, error: fetchError } = await supabaseClient
      .from('user_assessments')
      .select('id, status, completed_at, created_at')
      .eq('user_id', userId)
      .eq('assessment_name', assessmentName)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (fetchError && fetchError.code !== 'PGRST116') {
      throw fetchError;
    }

    if (existingAssessment) {
      return existingAssessment.id;
    }

    const { data: newAssessment, error: createError } = await supabaseClient
      .from('user_assessments')
      .insert({
        user_id: userId,
        assessment_name: assessmentName,
        status: 'in_progress'
      })
      .select('id')
      .maybeSingle();

    if (createError) {
      throw createError;
    }

    return newAssessment?.id || '';
  };

  const handleAnswerChange = (subdomainId: number, score: number) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [subdomainId]: score
    }));
  };

  const handleNext = async () => {
    if (currentSubdomain && selectedAnswers[currentSubdomain.id] !== undefined) {
      await saveCurrentAnswer();
    }
    
    if (isLastStep) {
      await handleComplete();
    } else {
      navigate(`/assessment/${domainId}/step/${currentStep + 1}`);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      navigate(`/assessment/${domainId}/step/${currentStep - 1}`);
    }
  };

  const handleGoBack = () => {
    navigate('/assessment');
  };

  const saveCurrentAnswer = async () => {
    if (!currentSubdomain || !userAssessmentId) return;
    
    setSaving(true);
    
    try {
      const selectedLevel = selectedAnswers[currentSubdomain.id];
      
      const { error } = await supabaseClient
        .from('user_responses')
        .upsert({
          user_assessment_id: userAssessmentId,
          subdomain_id: currentSubdomain.id,
          selected_level: selectedLevel
        }, {
          onConflict: 'user_assessment_id,subdomain_id'
        });

      if (error) {
        return false;
      }

      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!userAssessmentId) {
      return;
    }
    
    setSaving(true);
    
    try {
      const allAnswered = subdomains.every(subdomain => 
        selectedAnswers[subdomain.id] !== undefined
      );
      
      if (!allAnswered) {
        return;
      }
      
      const responseUpdates = Object.entries(selectedAnswers).map(([subdomainId, selectedLevel]) => ({
        user_assessment_id: userAssessmentId,
        subdomain_id: parseInt(subdomainId),
        selected_level: selectedLevel
      }));

      const { error: responsesError } = await supabaseClient
        .from('user_responses')
        .upsert(responseUpdates, {
          onConflict: 'user_assessment_id,subdomain_id'
        })
        .select('*');

      if (responsesError) {
        return;
      }

      const { data: verifyResponses, error: verifyResponsesError } = await supabaseClient
        .from('user_responses')
        .select('subdomain_id, selected_level')
        .eq('user_assessment_id', userAssessmentId);

      if (verifyResponsesError) {
        return;
      }
      
      if (verifyResponses?.length !== subdomains.length) {
        return;
      }

      const { error: assessmentError } = await supabaseClient
        .from('user_assessments')
        .update({ 
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', userAssessmentId)
        .select('*')
        .maybeSingle();

      if (assessmentError) {
        return;
      }

      const { data: verifyAssessment, error: verifyError } = await supabaseClient
        .from('user_assessments')
        .select('id, status, completed_at')
        .eq('id', userAssessmentId)
        .maybeSingle();

      if (verifyError) {
        return;
      }

      if (verifyAssessment?.status !== 'completed') {
        return;
      }

      toast.success(t('assessment.completedSuccessfully'));
      navigate('/assessment?completed=true');
    } catch {
      // Error handled silently
    } finally {
      setSaving(false);
    }
  };

  const getDescriptionOptions = (subdomain: Subdomain) => [
    { value: 1, label: subdomain.discovery_description },
    { value: 2, label: subdomain.foundational_description },
    { value: 3, label: subdomain.emerging_description },
    { value: 4, label: subdomain.developing_description },
    { value: 5, label: subdomain.advancing_description },
    { value: 6, label: subdomain.thriving_description },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-semibold">{t('assessment.loading')}</div>
        </div>
      </div>
    );
  }

  if (!currentSubdomain) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-semibold">{t('assessment.stepNotFound')}</div>
          <button
            onClick={handleGoBack}
            className="mt-4 bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            {t('common.goBack')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className='bg-white rounded-2xl px-6 py-4 font-bold shadow-assessment-sm'>
        <div className="mx-auto py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleGoBack}
              className="bg-button text-white px-4 py-2 rounded-lg hover:bg-button-hover transition-colors"
            >
              ← {t('common.goBack')}
            </button>
            
            <div className="text-center flex items-center">
              <h1 className="text-lg md:text-2xl font-bold mr-2">{domainName}</h1>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
            <div 
              className="bg-gray-800 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / subdomains.length) * 100}%` }}
            />
            <div className="text-sm text-gray-600 mt-1">
              {t('assessment.stepProgress', { current: currentStep, total: subdomains.length })}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-[20px]">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
          <div className="pt-3 px-4">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-semibold mb-2">{currentSubdomain.name}</h2>
                <p>{t('assessment.directions')}</p>
              </div>
              <span className="text-sm text-gray-600">{t('assessment.yourAnswer')}</span>
            </div>
          </div>

          <div className="divide-y divide-border-assessment [&>*:nth-child(2n)]:bg-white [&>*:nth-child(2n+1)]:bg-table-stripe">
            {getDescriptionOptions(currentSubdomain).map((option) => (
              <div
                key={option.value}
                className="flex items-center justify-between p-4 hover:bg-table-stripe cursor-pointer transition-colors"
              >
                <span className="text-sm text-radio-active font-semibold leading-relaxed flex-grow pr-4">
                  {option.label}
                </span>
                <Radio 
                  checked={selectedAnswers[currentSubdomain.id] === option.value}
                  onChange={() => handleAnswerChange(currentSubdomain.id, option.value)}
                />
              </div>
            ))}

            {currentSubdomain.not_applicable_description && (
              <div 
                className="flex items-center justify-between p-4 hover:bg-table-stripe cursor-pointer transition-colors"
              >
                <span className="text-gray-700 leading-relaxed flex-grow pr-4">
                  {t('assessment.notApplicable')}
                </span>
                <Radio 
                  checked={selectedAnswers[currentSubdomain.id] === 0}
                  onChange={() => handleAnswerChange(currentSubdomain.id, 0)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={handlePrevious}
          disabled={isFirstStep}
          className={`px-6 py-2 rounded-lg transition-colors ${
            isFirstStep
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {t('common.previous')}
        </button>

        <button
          onClick={handleNext}
          disabled={saving || selectedAnswers[currentSubdomain.id] === undefined}
          className={`px-6 py-2 rounded-lg transition-colors ${
            saving || selectedAnswers[currentSubdomain.id] === undefined
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-button text-white hover:bg-button-hover'
          }`}
        >
          {saving 
            ? t('assessment.saving')
            : isLastStep 
              ? t('assessment.completeAssessment')
              : t('common.next')
          }
        </button>
      </div>
      
      <p className="text-sm font-semibold text-radio-active mb-6">
        {t('assessment.helpText')}{' '}
        <span 
          onClick={() => setIsModalOpen(true)}
          title={t('assessment.clickForDetails')} 
          className="text-[#61c7c0] underline cursor-pointer font-bold"
        >
          HERE
        </span>
      </p>

      <LongAssessmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        currentSubdomain={currentSubdomain.name}
        currentDomain={domainName}
        assessmentData={longAssessments}
      />
    </div>
  );
};

export default AssessmentSteps;